// Reusable destructive-confirmation modal.
// Drop-in replacement for window.confirm() across the CRM.
//
// Usage:
//   const [open, setOpen] = useState(false);
//   <ConfirmDeleteModal
//     open={open}
//     onClose={() => setOpen(false)}
//     title="Delete lead?"
//     message={`"${lead.title}" will be soft-deleted. You can restore from the trash.`}
//     confirmLabel="Delete lead"
//     isPending={mutation.isPending}
//     onConfirm={() => mutation.mutate(id, { onSuccess: () => setOpen(false) })}
//   />
//
// Behaviour:
//  - Escape closes (handled by parent Modal)
//  - Backdrop click closes (Modal)
//  - Enter key triggers confirm (so keyboard users don't need mouse)
//  - Disabled Confirm during pending; Cancel always available unless pending
//  - Variant defaults to 'danger' (red button) — pass 'warning' for non-destructive bulk ops

import React, { useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import Modal from "./Modal";
import Button from "../atoms/Button";

export default function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title = "Delete?",
  message = "This action cannot be undone.",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  variant = "danger",
  isPending = false,
  detail, // optional second-line context, e.g. "5 related notes will be removed too"
}) {
  // Enter-to-confirm — only when modal is open + not already pending
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Enter" && !isPending) {
        e.preventDefault();
        onConfirm?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isPending, onConfirm]);

  const accent = variant === "danger" ? "var(--crm-danger)" : "var(--color-warning, #f59e0b)";
  const accentSoft = variant === "danger" ? "var(--crm-danger-soft)" : "rgba(245,158,11,0.10)";

  return (
    <Modal open={open} onClose={isPending ? undefined : onClose} width={460}>
      <div style={{ padding: "20px 22px 16px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div
            aria-hidden="true"
            style={{
              flexShrink: 0,
              width: 36, height: 36, borderRadius: 999,
              background: accentSoft,
              color: accent,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <AlertTriangle size={18} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 4 }}>
              {title}
            </div>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.45 }}>
              {message}
            </div>
            {detail ? (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 8, padding: "8px 10px", background: "var(--color-bg-elevated, rgba(0,0,0,0.03))", borderRadius: 6 }}>
                {detail}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "12px 22px 16px 22px",
          display: "flex", justifyContent: "flex-end", gap: 8,
          borderTop: "1px solid var(--color-border-subtle)",
          background: "var(--color-bg-elevated, transparent)",
        }}
      >
        <Button variant="secondary" size="md" onClick={onClose} disabled={isPending}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === "warning" ? "primary" : "danger"}
          size="md"
          onClick={onConfirm}
          disabled={isPending}
          leftIcon={isPending ? <Loader2 size={15} className="animate-spin" /> : null}
        >
          {isPending ? "Working…" : confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
